import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { Player } from '../types/api';

interface PlayerCircleProps {
  player: Player | null;
  position?: string; // Keep for backward compatibility but don't use
  size?: 'small' | 'medium' | 'large';
  showPosition?: boolean; // Keep for backward compatibility but don't use
  className?: string;
  color?: string | null;
  /** Speler is afgemeld voor deze wedstrijd: gedimd met een amberen ring. */
  isUnavailable?: boolean;
  /**
   * Maakt van de cirkel een keuzeknop: een tik roept dit aan in plaats van de hartjes.
   * Ook een lege cirkel wordt er aanklikbaar van - juist die is op een telefoon het
   * doelwit waar je een speler op wilt zetten.
   */
  onSelect?: () => void;
  /** Toelichting bij de knopvorm voor schermlezers; alleen zinvol met `onSelect`. */
  selectLabel?: string;
}

interface FloatingHeart {
  id: number;
  x: number;
  y: number;
  offsetX: number;
}

const PlayerCircle: React.FC<PlayerCircleProps> = ({
  player,
  size = 'medium',
  className = '',
  color = null,
  isUnavailable = false,
  onSelect,
  selectLabel
}) => {
  const { t } = useTranslation('playerView');
  const [hearts, setHearts] = useState<FloatingHeart[]>([]);
  const [heartId, setHeartId] = useState(0);
  const getJerseyNumber = (player: Player | null): string => {
    if (!player) return '?';
    return player.shirtNumber?.toString() || '⚠️';
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'small':
        return 'w-8 h-8 text-xs';
      case 'large':
        return 'w-16 h-16 text-lg';
      case 'medium':
      default:
        return 'w-12 h-12 text-sm';
    }
  };

  const handlePlayerClick = (event: React.MouseEvent<HTMLDivElement>) => {
    if (!player) return;
    
    // Get click position relative to the element
    const rect = event.currentTarget.getBoundingClientRect();
    const x = event.clientX - rect.left;
    const y = event.clientY - rect.top;
    
    // Add some random horizontal offset (-20px to +20px)
    const randomOffsetX = (Math.random() - 0.5) * 40;
    
    // Create new heart with random direction
    const newHeart = {
      id: heartId,
      x: x,
      y: y,
      offsetX: randomOffsetX
    };
    
    setHearts(prev => [...prev, newHeart]);
    setHeartId(prev => prev + 1);
    
    // Remove heart after animation completes
    setTimeout(() => {
      setHearts(prev => prev.filter(heart => heart.id !== newHeart.id));
    }, 2500);
  };

  const circleClasses = `${getSizeClasses()} text-white rounded-full flex items-center justify-center font-bold border-2 border-white shadow-lg ${isUnavailable ? 'opacity-60 ring-2 ring-amber-500 ring-offset-1' : ''}`;
  const circleStyle: React.CSSProperties = {
    backgroundColor: color || '#2A1C0D',
    border: color ? '2px solid rgba(255,255,255,0.9)' : undefined
  };

  return (
    <div className={`flex flex-col items-center ${className} relative`}>
      {/*
        Twee rollen voor dezelfde cirkel. Zonder `onSelect` is hij illustratie met een
        hartjes-easteregg eronder; met `onSelect` is hij het doelwit waarmee een coach op
        een telefoon een speler op deze plek zet. Dan hoort er ook echt een <button> te
        staan: een div met een klikhandler is voor toetsenbord en schermlezer geen knop,
        en dit is in die stand de enige weg naar de keuzelijst.
      */}
      {onSelect ? (
        <button
          type="button"
          onClick={onSelect}
          aria-label={selectLabel}
          className={`${circleClasses} cursor-pointer transition-transform duration-200 select-none hover:scale-110 active:scale-95 focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-hockey-blue focus-visible:ring-offset-2 motion-reduce:transition-none`}
          style={circleStyle}
        >
          {getJerseyNumber(player)}
        </button>
      ) : (
        <div
          className={`${circleClasses} ${player ? 'cursor-pointer hover:scale-110 transition-transform duration-200 select-none' : ''}`}
          style={circleStyle}
          title={isUnavailable ? t('lineup.unavailable') : undefined}
          onClick={handlePlayerClick}
        >
          {getJerseyNumber(player)}
        </div>
      )}
      {player && (
        <div className="mt-1 text-xs font-medium text-[#2A1C0D] bg-wit-vast px-2 py-0.5 rounded shadow select-none">
          {player.firstName}
        </div>
      )}
      
      {/* Floating Hearts */}
      {hearts.map((heart) => (
        <div
          key={heart.id}
          className="absolute pointer-events-none animate-float-heart text-red-500 text-lg"
          style={{
            left: heart.x,
            top: heart.y,
            transform: 'translate(-50%, -50%)',
            '--offset-x': `${heart.offsetX}px`
          } as React.CSSProperties}
        >
          ❤️
        </div>
      ))}
    </div>
  );
};

export default PlayerCircle;
